import React, { useState, useEffect } from 'react'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { useSubmitCustomRequestMutation, usePrepareOfferCheckoutMutation } from '../../redux/apis/endpoints/offerApi'
import { useUser } from '../../hooks/useUser'
import { showSuccess, showError } from '../../utils/toast'
import { formatCurrency } from '../../utils/formatters'
import PriceInputUI from '../input/PriceInputUI'
import RentDurationInputUI from '../input/RentDurationInputUI'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const PERIOD_OPTIONS = [
  { value: 'day', label: 'Days' },
  { value: 'week', label: 'Weeks' },
  { value: 'month', label: 'Months' },
]

// The payment form that actually submits to Stripe
const PaymentForm = ({ 
  clientSecret, 
  paymentIntentId, 
  onSuccess, 
  onCancel, 
  submitRequest, 
  isSubmittingRequest 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required', // We don't want to redirect if possible
    });

    if (error) {
      showError(error.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'requires_capture') {
      // Payment is authorized. Now submit the request to our backend.
      await submitRequest(paymentIntent.id);
      setIsProcessing(false);
    } else {
      showError('Unexpected payment status. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      <div className="flex flex-col gap-3">
        <Button
          type="submit"
          variant="primary"
          fullWidth
          isLoading={isProcessing || isSubmittingRequest}
          disabled={!stripe || isProcessing || isSubmittingRequest}
          className="!bg-emerald-600 hover:!bg-emerald-700 !rounded-full !py-3 !text-base !font-semibold"
        >
          Pay & Send Request
        </Button>
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={onCancel}
          disabled={isProcessing || isSubmittingRequest}
          className="!rounded-full !py-3"
        >
          Back
        </Button>
      </div>
      <p className="text-xs text-gray-500 text-center leading-relaxed">
        Your card will only be authorized. You won't be charged unless the designer accepts.
      </p>
    </form>
  );
};

const RequestFromDesignerModal = ({ isOpen, onClose, product }) => {
  const [activeTab, setActiveTab] = useState('rental')
  const { user } = useUser()
  const [submitCustomRequest, { isLoading: isSubmitting }] = useSubmitCustomRequestMutation()
  const [prepareOfferCheckout, { isLoading: isPreparing }] = usePrepareOfferCheckoutMutation()

  // Form state
  const [rentalPeriod, setRentalPeriod] = useState(5)
  const [rentalPeriodUnit, setRentalPeriodUnit] = useState('day')
  const [offerPrice, setOfferPrice] = useState('')
  const [message, setMessage] = useState('')
  const [commissionDescription, setCommissionDescription] = useState('')
  const [commissionBudget, setCommissionBudget] = useState('')
  const [commissionMessage, setCommissionMessage] = useState('')
  
  const [rentalError, setRentalError] = useState(null)
  const [commissionError, setCommissionError] = useState(null)

  // Payment state
  const [clientSecret, setClientSecret] = useState(null)
  const [paymentIntentId, setPaymentIntentId] = useState(null)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRentalPeriod(5)
      setRentalPeriodUnit('day')
      setOfferPrice('')
      setMessage('')
      setCommissionDescription('')
      setCommissionBudget('')
      setCommissionMessage('')
      setRentalError(null)
      setCommissionError(null)
      setClientSecret(null)
      setPaymentIntentId(null)
    }
  }, [isOpen])

  const config = product?.customRequestConfig || {}
  const rentalConfig = config.rentalOffer || {}
  const commissionConfig = config.commission || {}
  const currency = product?.currency || 'USD'

  // Determine available tabs
  const rentalEnabled = rentalConfig.enabled !== false
  const commissionEnabled = commissionConfig.enabled !== false

  // Auto-select available tab
  useEffect(() => {
    if (isOpen) {
      if (rentalEnabled) {
        setActiveTab('rental')
      } else if (commissionEnabled) {
        setActiveTab('commission')
      }
    }
  }, [isOpen, rentalEnabled, commissionEnabled])

  const handleProceedToPayment = async () => {
    if (!user) {
      showError('Please log in to send a request')
      return
    }

    try {
      let priceToAuthorize = 0;
      let requestType = '';

      if (activeTab === 'rental') {
        if (!offerPrice || parseFloat(offerPrice) <= 0) {
          setRentalError('Please enter an offer price')
          return
        }
        if (rentalConfig.minPrice && parseFloat(offerPrice) < parseFloat(rentalConfig.minPrice)) {
          setRentalError(`Offer price must be at least ${formatCurrency(rentalConfig.minPrice, currency)}`)
          return
        }
        priceToAuthorize = parseFloat(offerPrice);
        requestType = 'custom_rental';
      } else {
        if (!commissionBudget || parseFloat(commissionBudget) <= 0) {
          setCommissionError('Please enter a budget')
          return
        }
        if (commissionConfig.startingPrice && parseFloat(commissionBudget) < parseFloat(commissionConfig.startingPrice)) {
          setCommissionError(`Budget must be at least ${formatCurrency(commissionConfig.startingPrice, currency)}`)
          return
        }
        priceToAuthorize = parseFloat(commissionBudget);
        requestType = 'commission';
      }

      // Call prepare checkout API
      const res = await prepareOfferCheckout({
        offerPrice: priceToAuthorize,
        productId: product.id,
        type: requestType,
      }).unwrap();

      setClientSecret(res.clientSecret);
      setPaymentIntentId(res.paymentIntentId);

    } catch (err) {
      showError(err?.data?.message || 'Failed to prepare checkout. Please try again.')
    }
  }

  const handleFinalSubmit = async (confirmedPaymentIntentId) => {
    try {
      if (activeTab === 'rental') {
        await submitCustomRequest({
          type: 'custom_rental',
          product: product.id,
          offerPrice: parseFloat(offerPrice),
          message: message || null,
          rentOption: {
            number: rentalPeriod,
            option: rentalPeriodUnit,
          },
          paymentIntentId: confirmedPaymentIntentId,
        }).unwrap()
        showSuccess('Your rental offer has been sent!')
      } else {
        await submitCustomRequest({
          type: 'commission',
          product: product.id,
          offerPrice: parseFloat(commissionBudget),
          message: commissionMessage || null,
          commissionDetails: {
            description: commissionDescription,
          },
          paymentIntentId: confirmedPaymentIntentId,
        }).unwrap()
        showSuccess('Your commission request has been sent!')
      }
      onClose()
    } catch (err) {
      showError(err?.data?.message || 'Failed to send request. Please try again.')
    }
  }

  // Build the helper text for rental offers
  const getRentalHelperText = () => {
    const parts = []
    if (rentalConfig.minPrice) {
      parts.push(`${formatCurrency(rentalConfig.minPrice, currency)}`)
    }
    if (rentalConfig.shortestPeriod) {
      const unit = rentalConfig.shortestPeriodUnit || 'day'
      parts.push(`${rentalConfig.shortestPeriod}–30 ${unit}s`)
    }
    if (parts.length > 0) {
      return `Designer accepts offers from ${parts.join(' · ')}`
    }
    return null
  }

  const renderDetailsForm = () => (
    <div className="space-y-5">
      {/* Tab Buttons */}
      {rentalEnabled && commissionEnabled && (
        <div className="flex rounded-full bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('rental')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-full transition-all ${
              activeTab === 'rental'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Rental offer
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('commission')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-full transition-all ${
              activeTab === 'commission'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Made-to-order
          </button>
        </div>
      )}

      {/* Rental Offer Tab */}
      {activeTab === 'rental' && rentalEnabled && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Rental period
            </label>
            <RentDurationInputUI
              name="rentalPeriod"
              value={rentalPeriod}
              onChange={(e) => setRentalPeriod(e.target.value)}
              periodValue={rentalPeriodUnit}
              onPeriodChange={(period) => setRentalPeriodUnit(period)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Your offer
            </label>
            <PriceInputUI
              name="offerPrice"
              value={offerPrice}
              onChange={(e) => {
                setOfferPrice(e.target.value)
                if (rentalError) setRentalError(null)
              }}
              currency={currency}
              disableCurrencyChange={true}
            />
            {rentalError && (
              <p className="text-sm text-red-600 mt-1.5">{rentalError}</p>
            )}
            {getRentalHelperText() && !rentalError && (
              <p className="text-xs text-gray-500 mt-1.5">{getRentalHelperText()}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi! I'd love to rent this for an event on the 4th..."
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
            />
          </div>
        </div>
      )}

      {/* Commission Tab */}
      {activeTab === 'commission' && commissionEnabled && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              What would you like made?
            </label>
            <textarea
              value={commissionDescription}
              onChange={(e) => setCommissionDescription(e.target.value)}
              placeholder="I'd love this dress in a size M, in navy blue..."
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Your budget
            </label>
            <PriceInputUI
              name="commissionBudget"
              value={commissionBudget}
              onChange={(e) => {
                setCommissionBudget(e.target.value)
                if (commissionError) setCommissionError(null)
              }}
              currency={currency}
              disableCurrencyChange={true}
            />
            {commissionError && (
              <p className="text-sm text-red-600 mt-1.5">{commissionError}</p>
            )}
            {commissionConfig.startingPrice > 0 && !commissionError && (
              <p className="text-xs text-gray-500 mt-1.5">
                Starting price: {formatCurrency(commissionConfig.startingPrice, currency)}
                {commissionConfig.leadTime > 0 && (
                  <> · Typical lead time: {commissionConfig.leadTime} {commissionConfig.leadTimeUnit || 'week'}{commissionConfig.leadTime > 1 ? 's' : ''}</>
                )}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={commissionMessage}
              onChange={(e) => setCommissionMessage(e.target.value)}
              placeholder="Any additional details or preferences..."
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
            />
          </div>
        </div>
      )}

      <div className="w-full mt-6">
        <Button
          variant="primary"
          fullWidth
          onClick={handleProceedToPayment}
          isLoading={isPreparing}
          disabled={isPreparing}
          className="!bg-emerald-600 hover:!bg-emerald-700 !rounded-full !py-3 !text-base !font-semibold"
        >
          Continue to Payment
        </Button>
        <p className="text-xs text-gray-500 text-center mt-3 leading-relaxed">
          You'll authorize your card on the next step.
        </p>
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={clientSecret ? "Payment Authorization" : "Request from designer"}
      size="sm"
    >
      {!clientSecret ? (
        renderDetailsForm()
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentForm 
            clientSecret={clientSecret}
            paymentIntentId={paymentIntentId}
            onSuccess={() => {}}
            onCancel={() => setClientSecret(null)}
            submitRequest={handleFinalSubmit}
            isSubmittingRequest={isSubmitting}
          />
        </Elements>
      )}
    </Modal>
  )
}

export default RequestFromDesignerModal
